<script lang="ts" module>
  // TextField — the design-system labelled input (wiki/feature-design-system.md).
  export type TextFieldType =
    | 'text'
    | 'email'
    | 'password'
    | 'search'
    | 'number'
    | 'tel'
    | 'url'
    | 'date';
</script>

<script lang="ts">
  import type { FullAutoFill } from 'svelte/elements';

  interface Props {
    label: string;
    value: string;
    type?: TextFieldType;
    multiline?: boolean;
    rows?: number;
    placeholder?: string;
    error?: string;
    hint?: string;
    labelHidden?: boolean;
    required?: boolean;
    disabled?: boolean;
    id?: string;
    autocomplete?: FullAutoFill;
    maxlength?: number;
    min?: number | string;
    max?: number | string;
    step?: number | string;
    onInput?: (value: string) => void;
    onEnter?: () => void;
    onEscape?: () => void;
    onBlur?: () => void;
    ariaLabel?: string;
    'data-testid'?: string;
    class?: string;
  }

  let {
    label,
    value = $bindable(),
    type = 'text',
    multiline = false,
    rows = 3,
    labelHidden = false,
    placeholder,
    error,
    hint,
    required = false,
    disabled = false,
    id,
    autocomplete,
    maxlength,
    min,
    max,
    step,
    onInput,
    onEnter,
    onEscape,
    onBlur,
    ariaLabel,
    'data-testid': dataTestid,
    class: klass = '',
  }: Props = $props();

  const fallbackId = `vd-field-${Math.random().toString(36).slice(2, 9)}`;
  const fieldId = $derived(id ?? fallbackId);
  const hintId = $derived(`${fieldId}-hint`);
  const errorId = $derived(`${fieldId}-error`);

  const describedBy = $derived(error ? errorId : hint ? hintId : undefined);

  function handleInput(event: Event): void {
    onInput?.((event.currentTarget as HTMLInputElement | HTMLTextAreaElement).value);
  }

  function handleKeydown(event: KeyboardEvent): void {
    if (multiline) return;
    if (event.key === 'Enter' && onEnter) {
      onEnter();
    } else if (event.key === 'Escape' && onEscape) {
      // A click-to-edit inside a dialog cancels the EDIT, never the dialog.
      event.stopPropagation();
      event.preventDefault();
      onEscape();
    }
  }
</script>

<div class="vd-field {klass}" class:vd-field--error={!!error}>
  <label class="vd-field__label" class:vd-field__label--hidden={labelHidden} for={fieldId}>
    {label}{#if required}<span class="vd-field__req" aria-hidden="true"> *</span>{/if}
  </label>
  {#if multiline}
    <textarea
      id={fieldId}
      class="vd-field__control"
      {rows}
      {placeholder}
      {required}
      {disabled}
      {maxlength}
      aria-label={ariaLabel}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy}
      data-testid={dataTestid}
      bind:value
      oninput={handleInput}
    ></textarea>
  {:else}
    <input
      id={fieldId}
      {type}
      class="vd-field__control"
      {placeholder}
      {required}
      {disabled}
      {autocomplete}
      {maxlength}
      {min}
      {max}
      {step}
      aria-label={ariaLabel}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy}
      data-testid={dataTestid}
      bind:value
      oninput={handleInput}
      onkeydown={handleKeydown}
      onblur={() => onBlur?.()}
    />
  {/if}
  {#if error}
    <p id={errorId} class="vd-field__error" role="alert">{error}</p>
  {:else if hint}
    <p id={hintId} class="vd-field__hint">{hint}</p>
  {/if}
</div>

<style lang="scss">
  .vd-field {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .vd-field__label {
    font-family: var(--font-ui);
    font-size: var(--text-label);
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--ink-mute);
  }

  .vd-field__label--hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
  }

  .vd-field__req {
    color: var(--color-recording-text);
  }

  .vd-field__control {
    width: 100%;
    box-sizing: border-box;
    padding: var(--field-pad-y) var(--field-pad-x);
    font-family: var(--font-ui);
    font-size: var(--text-field);
    line-height: 1.5;
    color: var(--ink);
    background: var(--paper);
    border: 1px solid var(--line);
    border-radius: var(--field-radius);
  }

  textarea.vd-field__control {
    resize: vertical;
  }

  .vd-field__control:focus {
    outline: 2px solid var(--accent-text);
    outline-offset: 1px;
    border-color: var(--accent);
  }

  .vd-field__control:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  // The error ring is the semantic recording-red (theme-independent), NOT the
  // practice accent — an error is a fault, not an in-context prompt.
  .vd-field--error .vd-field__control {
    border-color: var(--color-recording);
  }

  .vd-field--error .vd-field__control:focus {
    outline-color: var(--color-recording);
  }

  .vd-field__error {
    margin: 0;
    font-family: var(--font-ui);
    font-size: var(--text-body);
    line-height: var(--text-body--line-height);
    color: var(--color-recording-text);
  }

  .vd-field__hint {
    margin: 0;
    font-family: var(--font-ui);
    font-size: var(--text-label);
    color: var(--ink-mute);
  }
</style>
